import './style.css';
import { audio } from './audio/audio.js';
import { Router } from './shell/router.js';
import { preloadHeavyScreens, SCREENS } from './shell/screens.js';
import { loadSave, writeSave } from './save/db.js';
import type { SaveData } from './core/save/model.js';

/**
 * Boot. Load the save, wire the router, and get out of the way.
 *
 * Nothing here may block on the network: the game is offline-first and is expected to launch from a
 * home-screen icon with the dev laptop switched off.
 */

const app = document.querySelector<HTMLElement>('#app');
if (app === null) throw new Error('#app missing from index.html');

const save = await loadSave();

/**
 * Writes are coalesced: a burst of region fills in the draw studio would otherwise hammer
 * IndexedDB. 400ms is short enough that a force-quit right after a save loses nothing she notices.
 */
const persist = debounce((data: SaveData) => {
  void writeSave(data).catch((error: unknown) => {
    console.error('[pawp] save failed', error);
  });
}, 400);

const router = new Router(app, SCREENS, save, persist);
router.start();

/*
 * The studio and the drive leg, fetched behind her back.
 *
 * They are no longer in the entry chunk - see `src/shell/screens.ts` - which is what lets the hub paint
 * without parsing Pixi and Planck first. That trade only holds if the code is there by the time she
 * presses something, so it is pulled in as soon as the browser is idle. `requestIdleCallback` where it
 * exists (not on Safari), a short timer where it does not; either way it is after first paint.
 */
if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(() => preloadHeavyScreens(), { timeout: 2000 });
} else {
  window.setTimeout(preloadHeavyScreens, 300);
}

/*
 * The gesture iOS insists on, taken from her first press wherever it lands.
 *
 * Safari will not create an AudioContext outside a real user gesture, which is the whole reason the game
 * used to open on a green disc with a play triangle on it. There is no such screen any more - it opens on
 * the Lookout - so the first press anywhere does the unlocking.
 *
 * Capture phase, so it runs before the button's own handler, and it does not preventDefault: it is riding
 * along with her press, not intercepting it. The bark happens *here*, after the unlock resolves, because
 * `unlock()` builds the buses after awaiting `resume()` - so the Hub's own bark on that same press finds
 * no audio graph yet and does nothing. This way her first press still answers.
 */
window.addEventListener(
  'pointerdown',
  () => {
    void audio.unlock().then(() => {
      audio.setVolumes(save.settings.musicVolume, save.settings.sfxVolume);
      audio.bark();
    });
  },
  { capture: true, once: true },
);

/*
 * Pausing is not wired here.
 *
 * This used to re-broadcast `visibilitychange` as `pawp:pause` / `pawp:resume`, and nothing ever listened:
 * the drive screen watches `visibilitychange` itself, and so do the pedals. Two names for one event, one of
 * them dead, is how a later reader concludes the pause lives here and adds the second half of it in the
 * wrong file. The pause she can see is `src/drive/pause-veil.ts`, mounted by the drive screen.
 */

if (import.meta.env.DEV) {
  const { mountDevOverlay } = await import('./dev/overlay.js');
  mountDevOverlay(app, router);
}

function debounce<T>(fn: (value: T) => void, ms: number): (value: T) => void {
  let handle: number | undefined;
  let pending: T;
  return (value: T) => {
    pending = value;
    if (handle !== undefined) window.clearTimeout(handle);
    handle = window.setTimeout(() => {
      handle = undefined;
      fn(pending);
    }, ms);
  };
}
